import {
  GroupSecurity,
  RelatedPersonRelationship,
} from '@quromedical/fhir-common';
import type { RelatedPersonGetResponse } from '@quromedical/models';
import { first } from '@quromedical/utils';
import type { PatientRouteParams } from 'app/routes';
import { PatientHeaderSection } from 'core/patient';
import { Tabs } from 'design-system';
import { useGlobalSearchParams } from 'expo-router';
import { useUserSession } from 'hooks/useUserSession';
import { NextOfKinApi } from 'integration/aggregate';
import type { PatientDrawerScreenProps } from 'navigation';
import type React from 'react';
import { useEffect, useState } from 'react';
import { ScrollView } from 'react-native';
import { NotFoundScreen } from 'screens/NotFoundScreen';
import { strings } from 'strings';

import {
  NextOfKinChatTab,
  PatientChatTab,
  PatientVideoConsultTab,
} from './tabs';

const api = new NextOfKinApi();

export const PatientCommunicationScreen: React.FC<
  PatientDrawerScreenProps<'Communication'>
> = () => {
  const { patientId } = useGlobalSearchParams<PatientRouteParams>();
  const session = useUserSession();
  const [relatedPerson, setRelatedPerson] = useState<
    RelatedPersonGetResponse[]
  >([]);
  const [hasChatPermissions, setHasChatPermissions] = useState(false);
  const [hasVideoPermissions, setHasVideoPermissions] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!patientId) {
        return;
      }
      const { relatedPersons } = await api.getAllNextOfKin({
        patient: patientId,
        relationship: RelatedPersonRelationship.NextOfKin,
      });
      setRelatedPerson(relatedPersons);

      const hasChatReadPermissions = session.hasAny([
        GroupSecurity.CommunicationRead,
      ]);
      const hasChatCreatePermissions = session.hasAny([
        GroupSecurity.CommunicationCreate,
      ]);

      // Only show WhatsApp chat if user has both read and create permissions
      // TODO: If user has chat read, we should show chat without a text and send box
      setHasChatPermissions(hasChatCreatePermissions && hasChatReadPermissions);

      setHasVideoPermissions(
        session.hasAny([
          GroupSecurity.VideoConsultCreate,
          GroupSecurity.VideoConsultInvite,
        ]),
      );
    };

    fetchData();
  }, [patientId, session]);

  if (!patientId) {
    return <NotFoundScreen />;
  }

  const relatedPersonId = first(relatedPerson)?.id;

  return (
    <ScrollView>
      <PatientHeaderSection id={patientId} />

      <Tabs.Group>
        <Tabs.Screen
          isHidden={!hasChatPermissions}
          title={strings.WhatsappChatPatient}
        >
          <PatientChatTab patientId={patientId} />
        </Tabs.Screen>

        <Tabs.Screen
          isHidden={!hasChatPermissions || !relatedPersonId}
          title={strings.WhatsappChatNextOfKin}
        >
          <NextOfKinChatTab
            nextOfKinId={relatedPersonId as string}
            patientId={patientId}
          />
        </Tabs.Screen>

        <Tabs.Screen
          isHidden={!hasVideoPermissions}
          title={strings.VideoConsult}
        >
          <PatientVideoConsultTab />
        </Tabs.Screen>
      </Tabs.Group>
    </ScrollView>
  );
};
